import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { StatCard } from "../../../ui/components/StatCard";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { useI18n } from "../../lib/i18n";
import { fetchInsightMetrics, type InsightMetrics } from "../../lib/api/insights";
import { formatUsd } from "../products/insights/InsightCard";
import * as table from "./InsightsUsagePage.css";
import * as s from "./PilotMetricsSection.css";

function pct(rate: number | null): string {
  return rate === null ? "—" : `${Math.round(rate * 100)}%`;
}

function share(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

function duration(minutes: number | null, t: (k: string, v?: Record<string, unknown>) => string): string {
  if (minutes === null) return "—";
  if (minutes < 60) return t("pro.system.insights.metrics.minutes", { n: Math.round(minutes) });
  if (minutes < 48 * 60) return t("pro.system.insights.metrics.hours", { n: (minutes / 60).toFixed(1) });
  return t("pro.system.insights.metrics.days", { n: (minutes / 1440).toFixed(1) });
}

export function PilotMetricsSection({
  token,
  period,
  organizations,
}: {
  token: string | null;
  period: { start?: string; end?: string };
  /** Organizations to offer in the filter, sorted by name. */
  organizations: Array<{ id: string; name: string }>;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = React.useState("");
  const [metrics, setMetrics] = React.useState<InsightMetrics | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchInsightMetrics(token, { ...period, organizationId: organizationId || undefined })
      .then((m) => {
        if (!cancelled) setMetrics(m);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t("pro.system.insights.metrics.loadFailed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, period.start, period.end, organizationId]);

  const m = metrics;
  const tt = t as (k: string, v?: Record<string, unknown>) => string;

  return (
    <div className={s.root}>
      <div className={s.controls}>
        <label htmlFor="pilot-metrics-org">{t("pro.system.insights.metrics.organization")}</label>
        <select
          id="pilot-metrics-org"
          className={s.select}
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
        >
          <option value="">{t("pro.system.insights.metrics.allOrganizations")}</option>
          {organizations.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>

      {error ? <Callout variant="error">{error}</Callout> : null}
      {loading && !m ? <AuthTransitionScreen variant="compact" message={t("pro.system.insights.loading")} /> : null}

      {m ? (
        <>
          <div className={s.tiles} aria-busy={loading}>
            <StatCard
              label={t("pro.system.insights.metrics.created")}
              value={m.engagement.created.toLocaleString()}
              subLabel={t("pro.system.insights.metrics.createdSub", {
                opened: m.engagement.opened,
                rate: pct(m.engagement.openRate),
              })}
              subLabelVariant="muted"
            />
            <StatCard
              label={t("pro.system.insights.metrics.timeToOpen")}
              value={duration(m.engagement.medianMinutesToOpen, tt)}
              subLabel={t("pro.system.insights.metrics.timeToOpenSub", { expired: m.engagement.expiredUnopened })}
              subLabelVariant="muted"
            />
            <StatCard
              label={t("pro.system.insights.metrics.analysisShown")}
              value={pct(share(m.engagement.openedWithAnalysis, m.engagement.opened))}
              subLabel={t("pro.system.insights.metrics.analysisShownSub", { n: m.engagement.openedWithoutAnalysis })}
              subLabelVariant="muted"
            />
            <StatCard
              label={t("pro.system.insights.metrics.helpful")}
              value={pct(m.feedback.helpfulRate)}
              subLabel={t("pro.system.insights.metrics.helpfulSub", {
                helpful: m.feedback.helpful,
                notHelpful: m.feedback.notHelpful,
              })}
              subLabelVariant="muted"
            />
            <StatCard
              label={t("pro.system.insights.metrics.support")}
              value={m.support.requests.toLocaleString()}
              subLabel={t("pro.system.insights.metrics.supportSub", {
                rate: pct(m.support.perOpenedInsight),
                handled: m.support.handled,
              })}
              subLabelVariant="muted"
            />
            <StatCard
              label={t("pro.system.insights.metrics.costPerOpened")}
              value={m.cost.perOpenedInsightUsd === null ? "—" : formatUsd(m.cost.perOpenedInsightUsd)}
              subLabel={t("pro.system.insights.metrics.costSub", {
                total: formatUsd(m.cost.costUsd),
                failed: m.cost.failedCalls,
              })}
              subLabelVariant="muted"
            />
          </div>

          {m.support.requests > 0 ? (
            <Text level="body-small" color="muted">
              {t("pro.system.insights.metrics.impactLine", {
                down: m.support.byImpact.down,
                degraded: m.support.byImpact.degraded,
                working: m.support.byImpact.working,
                handleTime: duration(m.support.medianHoursToHandle === null ? null : m.support.medianHoursToHandle * 60, tt),
              })}
            </Text>
          ) : null}

          <div>
            <h4 className={s.tableTitle}>{t("pro.system.insights.metrics.byCode")}</h4>
            <p className={s.tableHint}>{t("pro.system.insights.metrics.byCodeHint")}</p>
            {m.codes.length === 0 ? (
              <Text level="body-small" color="muted">
                {t("pro.system.insights.metrics.noInsights")}
              </Text>
            ) : (
              <div className={table.tableWrap}>
                <table className={table.table}>
                  <thead>
                    <tr>
                      <th className={table.th}>{t("pro.system.insights.guidance.col.code")}</th>
                      <th className={`${table.th} ${table.numeric}`}>{t("pro.system.insights.metrics.col.insights")}</th>
                      <th className={`${table.th} ${table.numeric}`}>{t("pro.system.insights.metrics.col.openRate")}</th>
                      <th className={`${table.th} ${table.numeric}`}>{t("pro.system.insights.metrics.col.noAnalysis")}</th>
                      <th className={`${table.th} ${table.numeric}`}>{t("pro.system.insights.metrics.col.helpful")}</th>
                      <th className={`${table.th} ${table.numeric}`}>{t("pro.system.insights.metrics.col.support")}</th>
                      <th className={table.th}>{t("pro.system.insights.metrics.col.guidance")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.codes.map((c) => (
                      <tr key={c.code}>
                        <td className={table.td} style={{ fontFamily: "monospace", fontWeight: 600 }}>
                          {c.code}
                        </td>
                        <td className={`${table.td} ${table.numeric}`}>{c.insights}</td>
                        <td className={`${table.td} ${table.numeric}`}>{pct(c.openRate)}</td>
                        <td className={`${table.td} ${table.numeric}`}>{c.openedWithoutAnalysis}</td>
                        <td className={`${table.td} ${table.numeric}`}>
                          {c.helpful + c.notHelpful > 0 ? `${pct(c.helpfulRate)} (${c.helpful + c.notHelpful})` : "—"}
                        </td>
                        <td className={`${table.td} ${table.numeric}`}>{c.supportRequests}</td>
                        <td className={table.td}>
                          {c.hasGuidance ? t("pro.system.insights.metrics.guidanceYes") : t("pro.system.insights.metrics.guidanceNo")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h4 className={s.tableTitle}>{t("pro.system.insights.metrics.notHelpfulTitle")}</h4>
            <p className={s.tableHint}>{t("pro.system.insights.metrics.notHelpfulHint")}</p>
            {m.notHelpful.length === 0 ? (
              <Text level="body-small" color="muted">
                {t("pro.system.insights.metrics.notHelpfulNone")}
              </Text>
            ) : (
              <div className={table.tableWrap}>
                <table className={table.table}>
                  <thead>
                    <tr>
                      <th className={table.th}>{t("pro.system.insights.metrics.col.insight")}</th>
                      <th className={table.th}>{t("pro.system.insights.support.col.product")}</th>
                      <th className={`${table.th} ${table.numeric}`}>{t("pro.system.insights.metrics.col.notHelpful")}</th>
                      <th className={`${table.th} ${table.numeric}`}>{t("pro.system.insights.metrics.col.helpfulVotes")}</th>
                      <th className={table.th}>{t("pro.system.insights.support.col.received")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.notHelpful.map((row) => (
                      <tr key={row.insightId}>
                        <td className={table.td}>
                          {row.title}
                          {row.alertCode ? ` · ${row.alertCode}` : ""}
                        </td>
                        <td className={table.td}>
                          <button
                            type="button"
                            className={s.link}
                            onClick={() =>
                              navigate({ to: "/workspace/products/$productId", params: { productId: row.productId } })
                            }
                          >
                            {row.productName ?? row.productId}
                          </button>
                        </td>
                        <td className={`${table.td} ${table.numeric}`}>{row.notHelpful}</td>
                        <td className={`${table.td} ${table.numeric}`}>{row.helpful}</td>
                        <td className={table.td}>{new Date(row.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
